import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  KeenIcon,
} from '@/components';
import { ImageCaptionDTO } from '@/types/api.types';
import { useSnackbar } from 'notistack';

interface MediaUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (media: (File | string)[], captions: ImageCaptionDTO[]) => void;
  allowedTypes: string[];
  maxFiles: number;
  initialMedia: (File | string)[];
  initialCaptions: ImageCaptionDTO[];
  guidelinesResolution?: string;
}

const MediaUploadModal = ({
  open,
  onClose,
  onSave,
  allowedTypes,
  maxFiles,
  initialMedia,
  initialCaptions,
  guidelinesResolution,
}: MediaUploadModalProps) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [currentMedia, setCurrentMedia] = useState<(File | string)[]>([]);
  const [captionsMap, setCaptionsMap] = useState<
    Record<number, Record<string, string>>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setCurrentMedia(initialMedia);
      const map: Record<number, Record<string, string>> = {};
      initialCaptions.forEach((c) => {
        if (c.image_index !== undefined && c.captions) {
          map[c.image_index] = c.captions;
        }
      });
      setCaptionsMap(map);
    }
  }, [open, initialMedia, initialCaptions]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
  const MIN_IMAGE_SIZE = 50 * 1024; // 50KB
  const MIN_VIDEO_SIZE = 1 * 1024 * 1024; // 1MB

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    if (currentMedia.length + fileArray.length > maxFiles) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'VALIDATION.MAX_FILES_EXCEEDED' },
          { max: maxFiles }
        ),
        { variant: 'error' }
      );
      return;
    }

    const invalidTypeFiles = fileArray.filter(
      (file) => !allowedTypes.includes(file.type)
    );

    if (invalidTypeFiles.length > 0) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'VALIDATION.INVALID_FILE_FORMAT' },
          {
            types: allowedTypes
              .map((type) => type.split('/')[1].toUpperCase())
              .join(', '),
          }
        ),
        { variant: 'error' }
      );
      return;
    }

    const hasInvalidImages = fileArray.some(
      (file) =>
        file.type.startsWith('image/') &&
        (file.size < MIN_IMAGE_SIZE || file.size > MAX_FILE_SIZE)
    );
    const hasInvalidVideos = fileArray.some(
      (file) =>
        file.type.startsWith('video/') &&
        (file.size < MIN_VIDEO_SIZE || file.size > MAX_VIDEO_SIZE)
    );

    if (hasInvalidImages) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'VALIDATION.FILE_SIZE_IMAGES' }),
        {
          variant: 'error',
        }
      );
    }
    if (hasInvalidVideos) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'VALIDATION.FILE_SIZE_VIDEOS' }),
        {
          variant: 'error',
        }
      );
    }

    if (hasInvalidImages || hasInvalidVideos) {
      return;
    }

    setCurrentMedia((prev) => [...prev, ...fileArray]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setCurrentMedia((prev) => prev.filter((_, i) => i !== index));

    // Update captions map and re-index
    const newCaptionsMap: Record<number, Record<string, string>> = {};
    Object.entries(captionsMap).forEach(([key, value]) => {
      const k = parseInt(key);
      if (k === index) return;
      if (k > index) {
        newCaptionsMap[k - 1] = value;
      } else {
        newCaptionsMap[k] = value;
      }
    });
    setCaptionsMap(newCaptionsMap);
  };

  const handleCaptionChange = (index: number, lang: string, value: string) => {
    setCaptionsMap((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [lang]: value,
      },
    }));
  };

  const handleSave = () => {
    // Validate that all media have EN and FI captions
    const missingCaptions: number[] = [];
    currentMedia.forEach((_, index) => {
      const captions = captionsMap[index];
      if (!captions?.en || !captions?.fi) {
        missingCaptions.push(index + 1);
      }
    });

    const hasInvalidImages = currentMedia.some(
      (item) =>
        item instanceof File &&
        item.type.startsWith('image/') &&
        (item.size < MIN_IMAGE_SIZE || item.size > MAX_FILE_SIZE)
    );
    const hasInvalidVideos = currentMedia.some(
      (item) =>
        item instanceof File &&
        item.type.startsWith('video/') &&
        (item.size < MIN_VIDEO_SIZE || item.size > MAX_VIDEO_SIZE)
    );

    if (hasInvalidImages) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'VALIDATION.FILE_SIZE_IMAGES' }),
        {
          variant: 'error',
        }
      );
    }
    if (hasInvalidVideos) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'VALIDATION.FILE_SIZE_VIDEOS' }),
        {
          variant: 'error',
        }
      );
    }

    if (hasInvalidImages || hasInvalidVideos) {
      return;
    }

    if (missingCaptions.length > 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'HARBOR.FORM.CAPTION_EN_REQUIRED' }) +
          ' & ' +
          intl.formatMessage({ id: 'HARBOR.FORM.CAPTION_FI_REQUIRED' }) +
          ` (${intl.formatMessage({ id: 'HARBOR.FORM.MEDIA_PREVIEW_ALT' }, { index: missingCaptions.join(', ') })})`,
        { variant: 'error' }
      );
      return;
    }

    const finalCaptions: ImageCaptionDTO[] = Object.entries(captionsMap)
      .filter(([index]) => parseInt(index) < currentMedia.length)
      .map(([index, captions]) => ({
        image_index: parseInt(index),
        captions,
      }));
    onSave(currentMedia, finalCaptions);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="media-upload-modal-title"
      disableEnforceFocus={false}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <ModalContent className="bg-white dark:bg-gray-100 contrast:bg-gray-100 rounded-xl shadow-xl w-full max-w-[800px] max-h-[90vh] flex flex-col overflow-hidden">
        <ModalHeader className="border-b border-gray-200 dark:border-gray-700 contrast:border-gray-700 px-6 py-4 flex-shrink-0">
          <ModalTitle
            id="media-upload-modal-title"
            className="text-xl font-bold text-gray-900 dark:text-white contrast:text-white"
          >
            {intl.formatMessage({ id: 'HARBOR.FORM.UPLOAD_MEDIA' })}
          </ModalTitle>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-800 contrast:text-gray-800 hover:text-gray-700 dark:hover:text-gray-200 contrast:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
            aria-label={intl.formatMessage({ id: 'COMMON.CLOSE' })}
          >
            <KeenIcon icon="cross" className="text-xl" />
          </button>
        </ModalHeader>
        <ModalBody className="p-6 overflow-y-auto flex-grow custom-scrollbar">
          <div className="space-y-6">
            {/* Upload Guidelines Panel */}
            <div className="bg-blue-50 dark:bg-blue-900/20 contrast:bg-blue-900/20 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-blue-900 dark:text-blue-100 contrast:text-blue-100 mb-3 flex items-center gap-2">
                <KeenIcon icon="information-2" className="text-lg" />
                {intl.formatMessage({
                  id: 'HARBOR.FORM.UPLOAD_GUIDELINES_TITLE',
                })}
              </h2>
              <div className="space-y-2 text-xs text-blue-800 dark:text-blue-200 contrast:text-blue-200">
                <p className="flex items-start gap-2">
                  <KeenIcon
                    icon="check-circle"
                    className="text-sm mt-0.5 flex-shrink-0"
                  />
                  <span>
                    {intl.formatMessage({
                      id: 'HARBOR.FORM.UPLOAD_GUIDELINES_FORMAT',
                    })}
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <KeenIcon
                    icon="check-circle"
                    className="text-sm mt-0.5 flex-shrink-0"
                  />
                  <span>
                    {guidelinesResolution ||
                      intl.formatMessage({
                        id: 'HARBOR.FORM.UPLOAD_GUIDELINES_RESOLUTION',
                      })}
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <KeenIcon
                    icon="check-circle"
                    className="text-sm mt-0.5 flex-shrink-0"
                  />
                  <span>
                    {intl.formatMessage({
                      id: 'HARBOR.FORM.UPLOAD_GUIDELINES_SIZE_IMAGES',
                    })}
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <KeenIcon
                    icon="check-circle"
                    className="text-sm mt-0.5 flex-shrink-0"
                  />
                  <span>
                    {intl.formatMessage({
                      id: 'HARBOR.FORM.UPLOAD_GUIDELINES_SIZE_VIDEOS',
                    })}
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <KeenIcon
                    icon="check-circle"
                    className="text-sm mt-0.5 flex-shrink-0"
                  />
                  <span className="font-semibold">
                    {intl.formatMessage({
                      id: 'HARBOR.FORM.UPLOAD_GUIDELINES_CAPTIONS',
                    })}
                  </span>
                </p>
              </div>
            </div>

            {/* WCAG Guidelines Panel */}
            <div className="bg-green-50 dark:bg-green-900/20 contrast:bg-green-900/20 border border-green-200 dark:border-green-800 contrast:border-green-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 contrast:text-green-100 mb-3 flex items-center gap-2">
                <KeenIcon icon="shield-tick" className="text-lg" />
                {intl.formatMessage({
                  id: 'HARBOR.FORM.UPLOAD_GUIDELINES_WCAG_TITLE',
                })}
              </h3>
              <div className="space-y-1.5 text-xs text-green-800 dark:text-green-200 contrast:text-green-200">
                <p>
                  {intl.formatMessage({
                    id: 'HARBOR.FORM.UPLOAD_GUIDELINES_WCAG_ALT',
                  })}
                </p>
                <p>
                  {intl.formatMessage({
                    id: 'HARBOR.FORM.UPLOAD_GUIDELINES_WCAG_CONTEXT',
                  })}
                </p>
                <p>
                  {intl.formatMessage({
                    id: 'HARBOR.FORM.UPLOAD_GUIDELINES_WCAG_DECORATIVE',
                  })}
                </p>
                <p>
                  {intl.formatMessage({
                    id: 'HARBOR.FORM.UPLOAD_GUIDELINES_WCAG_CONTRAST',
                  })}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 contrast:border-gray-600 rounded-lg p-8 bg-gray-50 dark:bg-gray-100 contrast:bg-gray-100">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept={allowedTypes.join(',')}
                className="hidden"
                id="modal-file-input"
                data-testid="modal-file-input"
              />
              <KeenIcon
                icon="file-up"
                className="text-4xl text-gray-400 dark:text-gray-500 contrast:text-gray-500 mb-4"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-primary"
                aria-controls="modal-file-input"
              >
                {intl.formatMessage({ id: 'HARBOR.FORM.SELECT_FILES' })}
              </button>
              <p className="text-sm text-gray-500 dark:text-gray-800 contrast:text-gray-800 mt-2">
                {intl.formatMessage(
                  { id: 'HARBOR.FORM.MAX_FILES_ALLOWED' },
                  { max: maxFiles }
                )}
              </p>
            </div>

            {currentMedia.length > 0 && (
              <div className="space-y-4">
                {currentMedia.map((item, index) => {
                  const src =
                    item instanceof File ? URL.createObjectURL(item) : item;
                  const isVideo =
                    (item instanceof File && item.type.startsWith('video/')) ||
                    (typeof item === 'string' && item.endsWith('.mp4'));

                  return (
                    <div
                      key={index}
                      data-testid={`media-preview-${index}`}
                      className="flex gap-4 p-4 bg-gray-50 dark:bg-gray-100 contrast:bg-gray-100 rounded-lg border border-gray-200 dark:border-gray-700 contrast:border-gray-700"
                    >
                      <div className="relative w-24 h-24 flex-shrink-0">
                        {isVideo ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700 contrast:bg-gray-700 rounded-lg">
                            <KeenIcon icon="video" className="text-2xl" />
                          </div>
                        ) : (
                          <img
                            src={src}
                            alt={intl.formatMessage(
                              { id: 'HARBOR.FORM.MEDIA_PREVIEW_ALT' },
                              { index: index + 1 }
                            )}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                          aria-label={intl.formatMessage(
                            { id: 'HARBOR.FORM.REMOVE_MEDIA_INDEX' },
                            { index: index + 1 }
                          )}
                        >
                          <KeenIcon icon="cross" className="text-xs" />
                        </button>
                      </div>
                      <div className="flex-grow grid grid-cols-1 gap-3">
                        <div>
                          <label
                            htmlFor={`caption-en-${index}`}
                            className="text-xs font-semibold text-gray-500 dark:text-gray-800 contrast:text-gray-800 uppercase mb-1 block"
                          >
                            {intl.formatMessage({
                              id: 'HARBOR.FORM.CAPTION_EN',
                            })}
                            <span
                              className="text-red-500 ml-1"
                              aria-label="required"
                            >
                              *
                            </span>
                          </label>
                          <input
                            id={`caption-en-${index}`}
                            type="text"
                            data-testid={`caption-en-${index}`}
                            value={captionsMap[index]?.en || ''}
                            onChange={(e) =>
                              handleCaptionChange(index, 'en', e.target.value)
                            }
                            className="input input-sm w-full"
                            autoComplete="off"
                            placeholder={intl.formatMessage({
                              id: 'HARBOR.FORM.ENTER_CAPTION_EN',
                            })}
                            required
                            aria-required="true"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label
                              htmlFor={`caption-fi-${index}`}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-800 contrast:text-gray-800 uppercase mb-1 block"
                            >
                              {intl.formatMessage({
                                id: 'HARBOR.FORM.CAPTION_FI',
                              })}
                              <span
                                className="text-red-500 ml-1"
                                aria-label="required"
                              >
                                *
                              </span>
                            </label>
                            <input
                              id={`caption-fi-${index}`}
                              type="text"
                              value={captionsMap[index]?.fi || ''}
                              onChange={(e) =>
                                handleCaptionChange(index, 'fi', e.target.value)
                              }
                              className="input input-sm w-full"
                              autoComplete="off"
                              placeholder={intl.formatMessage({
                                id: 'HARBOR.FORM.ENTER_CAPTION_FI',
                              })}
                              required
                              aria-required="true"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`caption-sv-${index}`}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-800 contrast:text-gray-800 uppercase mb-1 block"
                            >
                              {intl.formatMessage({
                                id: 'HARBOR.FORM.CAPTION_SV',
                              })}
                            </label>
                            <input
                              id={`caption-sv-${index}`}
                              type="text"
                              value={captionsMap[index]?.sv || ''}
                              onChange={(e) =>
                                handleCaptionChange(index, 'sv', e.target.value)
                              }
                              className="input input-sm w-full"
                              autoComplete="off"
                              placeholder={intl.formatMessage({
                                id: 'HARBOR.FORM.ENTER_CAPTION_SV',
                              })}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`caption-de-${index}`}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-800 contrast:text-gray-800 uppercase mb-1 block"
                            >
                              {intl.formatMessage({
                                id: 'HARBOR.FORM.CAPTION_DE',
                              })}
                            </label>
                            <input
                              id={`caption-de-${index}`}
                              type="text"
                              value={captionsMap[index]?.de || ''}
                              onChange={(e) =>
                                handleCaptionChange(index, 'de', e.target.value)
                              }
                              className="input input-sm w-full"
                              autoComplete="off"
                              placeholder={intl.formatMessage({
                                id: 'HARBOR.FORM.ENTER_CAPTION_DE',
                              })}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label
                              htmlFor={`caption-copyright-${index}`}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-800 contrast:text-gray-800 uppercase mb-1 block"
                            >
                              {intl.formatMessage({
                                id: 'HARBOR.FORM.COPYRIGHT',
                              })}
                            </label>
                            <input
                              id={`caption-copyright-${index}`}
                              type="text"
                              value={captionsMap[index]?.copyright || ''}
                              onChange={(e) =>
                                handleCaptionChange(
                                  index,
                                  'copyright',
                                  e.target.value
                                )
                              }
                              className="input input-sm w-full"
                              autoComplete="off"
                              placeholder={intl.formatMessage({
                                id: 'HARBOR.FORM.ENTER_COPYRIGHT',
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ModalBody>
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 contrast:border-gray-700 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn btn-light">
            {intl.formatMessage({ id: 'COMMON.CANCEL' })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
          >
            {intl.formatMessage({ id: 'COMMON.SAVE' })}
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default MediaUploadModal;
